import { useEffect } from 'react';
import config from '../config';
import { useAuth } from './useAuth';

// ---- Constants ----
const APP_ID =
  'DvRvStPZG9uOmNvcmU6ZHZydi1ldS0xOmRldm8vM21oZGpDQ1Y5OTpwbHVnX3NldHRpbmcvMV9ffHxfXzIwMjUtMDQtMjcgMTE6NDE6NTAuMTkwNDg5OTU1ICswMDAwIFVUQw==xlxendsDvRv';
const ON_PLUG_WIDGET_READY = 'ON_PLUG_WIDGET_READY';

// ---- Types ----
declare global {
  interface Window {
    plugSDK: PlugSDK;
  }
}

interface PlugSDK {
  init: (options: { app_id: string; session_token?: string }) => void;
  _initialized?: boolean;
  shutdown?: () => Promise<void>;
  toggleWidget?: (
    toggle: boolean,
    widget: string,
    options?: { startConversationContent?: string }
  ) => Promise<void>;
  onEvent?: (cb: (payload: { type: string }) => void) => void;
}

// ---- Helpers ----
function shouldOpenSupportChat() {
  const params = new URLSearchParams(window.location.search);
  return params.get('support_chat') === 'true';
}

function openSupportChatIfNeeded() {
  if (shouldOpenSupportChat()) {
    const sdk = window.plugSDK;
    if (sdk && typeof sdk.toggleWidget === 'function') {
      try {
        sdk.toggleWidget(true, 'create_conversation');
      } catch (e) {
        console.error('Error opening support chat', e);
      }
    }
  }
}

// ---- Main Hook ----
export default function usePlugSDK() {
  const { token } = useAuth();

  useEffect(() => {
    let callbackCalled = false;

    // Initialize the plugSDK if needed
    async function initPlugSDK() {
      if (window.plugSDK && !window.plugSDK._initialized && token) {
        try {
          const response = await fetch(
            `${config.API_BASE_URL}/portal/v2/devrev-session-token`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          const data = await response.json();
          window.plugSDK.init({
            app_id: APP_ID,
            session_token: data.session_token?.access_token,
          });
        } catch (e) {
          // fallback to just app_id if token fetch fails
          console.error('Plug support chat initalization error: ', e);
          window.plugSDK.init({ app_id: APP_ID });
        }
        window.plugSDK._initialized = true;
      }

      // Listen for widget ready event and open support chat if needed
      if (window.plugSDK && typeof window.plugSDK.onEvent === 'function') {
        window.plugSDK.onEvent((payload: { type: string }) => {
          if (!callbackCalled && payload.type === ON_PLUG_WIDGET_READY) {
            callbackCalled = true;
            openSupportChatIfNeeded();
          }
        });
      }
    }

    initPlugSDK();
  }, [token]);

  return null;
}

// ---- Utility Export ----
export function getPlugSDK() {
  return window.plugSDK;
}